import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import AlertInline from '@storeblocks/inline-alert';
import { Input } from '@storeblocks/input';
import { RadioProps } from '@storeblocks/radio-atom';
import { RadioButtons } from '@storeblocks/radio-buttons';
import { Formik, FormikProps } from 'formik';
import { type FC, useState } from 'react';

import {
  useUpdateUserSettingsMutation,
  useUserSettingsQuery,
} from '@/api/userSettings/userSettingsApi';
import { getAuth } from '@/auth/Auth';
import { FormValidationSummary } from '@/components/Form/FormValidationSummary';
import { formHasErrors } from '@/components/Form/utils/formHasErrors';
import { Column, Row } from '@/components/Grid';
import { isInternalUser } from '@/config/utils';
import { useTexts } from '@/hooks/useTexts';
import { Language } from '@/i18n/Language';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { UserSettingsFormData } from './UserSettingsFormData';
import { useUserSettingsValidationSchema } from './useUserSettingsValidationSchema';

interface Props {
  forceUpdate?: boolean;
}

export const UserSettingsForm: FC<Props> = ({ forceUpdate }) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  // For internal users we already know their email address.
  // Prefill the email field with their '@storebrand' email
  const initialEmailAddress = isInternalUser()
    ? getAuth().getTokenParsed().email || ''
    : '';

  const validationSchema = useUserSettingsValidationSchema(texts);

  const userSettings = useUserSettingsQuery();
  const [patchUserSettings, userSettingsMutation] =
    useUpdateUserSettingsMutation();

  const [initialValues, setInitialValues] = useState<UserSettingsFormData>({
    email: userSettings.data?.emailBusiness || initialEmailAddress,
    language: userSettings.data?.language || Language.English,
  });

  const languageOptions: RadioProps[] = [
    {
      id: Language.Norwegian,
      label: texts.language.noLabel,
      value: Language.Norwegian,
    },
    {
      id: Language.English,
      label: texts.language.enLabel,
      value: Language.English,
    },
    {
      id: Language.Swedish,
      label: texts.language.svLabel,
      value: Language.Swedish,
    },
  ];

  const handleSubmit = async (values: UserSettingsFormData): Promise<void> => {
    await patchUserSettings({
      emailBusiness: values.email,
      language: values.language,
    });

    userSettings.refetch();

    // Reset the form with new initial values after submission.
    setInitialValues(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        errors,
        dirty,
        submitCount,
        submitForm,
        getFieldProps,
      }: FormikProps<UserSettingsFormData>) => (
        <>
          <Row>
            <Column $span={6}>
              <Input
                id="email"
                type="email"
                label={texts.userSettings.changeEmail}
                hint={{ text: texts.userSettings.contactDetailsDescription }}
                alert={{
                  messageType: errors.email ? 'error' : undefined,
                  message: errors.email,
                }}
                fluid
                {...getFieldProps('email')}
              />
            </Column>
          </Row>
          <RadioButtons
            groupName="language"
            data-test="language-selector"
            legendProps={{
              title: texts.pages.forceUpdateInformation.language.title,
            }}
            options={languageOptions.map((option) => ({
              ...option,
              defaultChecked: getFieldProps('language').value === option.value,
            }))}
            alertMessageType={errors.language ? 'error' : undefined}
            alertMessage={errors.language}
            {...getFieldProps('language')}
          />
          <div>
            <Button
              id="submit"
              type="submit"
              loading={isSubmitting}
              data-trackid={getTrackId(
                'save-user-settings',
                TrackingElement.Button,
              )}
              onClick={submitForm}
            >
              {texts.pages.userSettings.save}
            </Button>
          </div>

          {submitCount > 0 && formHasErrors(errors) && (
            <FormValidationSummary
              title={texts.pages.userSettings.validationSummary.title}
              errors={errors}
            />
          )}

          {userSettingsMutation.isError && (
            <Alert fluid variant="error" description={texts.genericError} />
          )}

          {!dirty && userSettingsMutation.isSuccess && (
            <AlertInline
              data-test="success-alert"
              variant="success"
              title={
                forceUpdate
                  ? texts.pages.forceUpdateInformation.confirm
                  : texts.pages.userSettings.success
              }
            />
          )}
        </>
      )}
    </Formik>
  );
};
